import { useCallback, useMemo } from 'react';

import {
  InfiniteData,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { simulationsEndpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut, type ApiError } from '@/api/fetcher';
import useBrains from '@/hooks/useBrains';
import { useScopedCache } from '@/hooks/useScopedCache';
import { insightsCache } from '@/modules/simulations/insightsCache';
import type {
  EvaluationInsightsResponse,
  EvaluationsListResponse,
  EvaluationWithSimulation,
  NewSimulation,
  RunsListParams,
  RunsListResponse,
  Simulation,
  SimulationRun,
  SimulationsListResponse,
} from '@/modules/simulations/types';
import { isEvaluationInProgress, listCategories } from '@/modules/simulations/utils';
import { selectAccountSlug } from '@/redux/session/selectors';
import { selectUserId } from '@/redux/user/selectors';
import { EventName } from '@/shared/types/segment';
import { delay } from '@/util/async';
import { BULK_DELETE_CONCURRENCY, runWithConcurrency } from '@/util/concurrency';

export const FIVE_SECONDS = 5_000;

export const API = Object.freeze({
  listSimulations: async (brainId: string): Promise<SimulationsListResponse> =>
    callGet(endpoints.simulations(brainId)),

  getSimulation: async (brainId: string, id: string): Promise<Simulation> =>
    callGet(endpoints.simulation(brainId, id)),

  createSimulation: async (brainId: string, body: NewSimulation): Promise<Simulation> =>
    callPost(endpoints.simulations(brainId), body),

  updateSimulation: async (
    brainId: string,
    id: string,
    body: Partial<NewSimulation>
  ): Promise<Simulation> => callPut(endpoints.simulation(brainId, id), body),

  deleteSimulation: async (brainId: string, id: string): Promise<void> =>
    callDelete(endpoints.simulation(brainId, id)),

  listRuns: async (
    brainId: string,
    params?: RunsListParams & { cursor?: string }
  ): Promise<RunsListResponse> => callGet(endpoints.runs(brainId, params)),

  getRun: async (brainId: string, runId: string): Promise<SimulationRun> =>
    callGet(endpoints.run(brainId, runId)),

  listEvaluations: async (brainId: string, runId: string): Promise<EvaluationsListResponse> =>
    callGet(endpoints.evaluations(brainId, runId)),

  runEvaluationInsights: async (
    brainId: string,
    runId: string,
    evaluationId: string
  ): Promise<EvaluationInsightsResponse> =>
    callPost(endpoints.evaluationInsights(brainId, runId, evaluationId), {}),

  triggerRun: async (
    brainId: string,
    body?: { simulation_ids?: string[]; filter_category?: string }
  ): Promise<SimulationRun> => callPost(endpoints.runs(brainId), body ?? {}),

  cancelRun: async (brainId: string, runId: string): Promise<void> =>
    callPost(endpoints.cancelRun(brainId, runId), {}),

  deleteRun: async (brainId: string, runId: string): Promise<void> =>
    callDelete(endpoints.run(brainId, runId)),
});

// Cache helpers
const onSimulationRemoved = (queryClient: QueryClient, brainId: string, simulationId: string) => {
  const key = [endpoints.simulations(brainId)];
  queryClient.setQueryData<SimulationsListResponse>(key, (prev) => ({
    ...prev,
    simulations: (prev?.simulations ?? []).filter((s) => s.simulation_id !== simulationId),
  }));
  // Sweep every cached runs/run/evaluations query — any could be stale after a sim delete.
  const runsPrefix = endpoints.runs(brainId).split('?')[0];
  queryClient.invalidateQueries({
    predicate: (query) => {
      const head = query.queryKey[0];
      return typeof head === 'string' && head.startsWith(runsPrefix);
    },
  });
};

const onRunRemoved = (queryClient: QueryClient, brainId: string, runId: string) => {
  // Prefix-match every cached filter variant — runs query keys carry status/with_fails/etc.
  queryClient.setQueriesData<InfiniteData<RunsListResponse>>(
    { queryKey: [endpoints.runs(brainId)] },
    (prev) =>
      prev && {
        ...prev,
        pages: prev.pages.map((page) => ({
          ...page,
          runs: page.runs.filter((r) => r.run_id !== runId),
        })),
      }
  );
  queryClient.invalidateQueries({ queryKey: [endpoints.runs(brainId)] });
  queryClient.removeQueries({ queryKey: [endpoints.run(brainId, runId)] });
  queryClient.removeQueries({ queryKey: [endpoints.evaluations(brainId, runId)] });
};

export const isRunTerminal = (status?: string) =>
  status === 'completed' || status === 'failed' || status === 'canceled';

// Polls until the run reaches a terminal status. Outlives the calling component —
// the captured queryClient is the app-wide singleton, so this keeps running across
// page navigation and resolves whenever the run finishes.
const waitForTerminal = async (queryClient: QueryClient, brainId: string, runId: string) => {
  while (true) {
    const run = await queryClient.fetchQuery<SimulationRun>({
      queryKey: [endpoints.run(brainId, runId)],
      queryFn: () => API.getRun(brainId, runId),
      staleTime: 0,
    });
    if (isRunTerminal(run.status)) {
      return run;
    }
    await delay(FIVE_SECONDS);
  }
};

const useSimulations = (brainId?: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const currentUserId = useSelector(selectUserId);

  const navigateToRun = useCallback(
    (runId: string) => navigate(`/${slug}/ai-agents/${brainId}/simulations/runs/${runId}`),
    [navigate, slug, brainId]
  );

  // --- Queries ---

  const {
    data: simulationsData,
    status: listStatus,
    isLoading: isSimulationsLoading,
  } = useQuery<SimulationsListResponse>({
    queryKey: [endpoints.simulations(brainId!)],
    queryFn: () => API.listSimulations(brainId!),
    enabled: !!brainId,
  });

  // --- Mutations ---

  const { mutate: createSimulation, status: createStatus } = useMutation<
    Simulation,
    Error,
    NewSimulation,
    { previousData: SimulationsListResponse | undefined }
  >({
    mutationFn: (body) => API.createSimulation(brainId!, body),
    meta: {
      segment: {
        success: EventName.SimulationCreated,
        action: 'simulation_create',
        successProps: (simulation: Simulation) => ({
          simulation_id: simulation.simulation_id,
          brain_id: simulation.brain_id,
        }),
      },
    },
    onMutate: async (body) => {
      const key = [endpoints.simulations(brainId!)];
      await queryClient.cancelQueries({ queryKey: key });
      const previousData = queryClient.getQueryData<SimulationsListResponse>(key);

      // Optimistic insert with a temporary placeholder
      const optimistic: Simulation = {
        simulation_id: `temp-${Date.now()}`,
        brain_id: brainId!,
        created_by: currentUserId ?? null,
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
        ...body,
      };
      queryClient.setQueryData<SimulationsListResponse>(key, (prev) => ({
        ...prev,
        simulations: [optimistic, ...(prev?.simulations ?? [])],
      }));

      return { previousData };
    },
    onSuccess: (resp) => {
      // Replace optimistic data with real server response
      queryClient.invalidateQueries({ queryKey: [endpoints.simulations(brainId!)] });
      // schedule_frequency may have attached this sim to a managed schedule server-side
      queryClient.invalidateQueries({ queryKey: [endpoints.schedules(brainId!)] });
      toast.success(t('simulations.created', { 0: resp.name }));
    },
    onError: (error, _body, context) => {
      // Rollback on failure
      if (context) {
        queryClient.setQueryData([endpoints.simulations(brainId!)], context.previousData);
      }
      toast.error(error.message);
    },
  });

  const { mutate: updateSimulation, status: updateStatus } = useMutation<
    Simulation,
    Error,
    { id: string; body: Partial<NewSimulation> }
  >({
    mutationFn: ({ id, body }) => API.updateSimulation(brainId!, id, body),
    onSuccess: (resp) => {
      queryClient.setQueryData<SimulationsListResponse>(
        [endpoints.simulations(brainId!)],
        (prev) => ({
          ...prev,
          simulations: (prev?.simulations ?? []).map((s) =>
            s.simulation_id === resp.simulation_id ? resp : s
          ),
        })
      );
      queryClient.invalidateQueries({ queryKey: [endpoints.schedules(brainId!)] });
      toast.success(t('simulations.updated', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const { mutate: deleteSimulation, status: deleteStatus } = useMutation<void, Error, string>({
    mutationFn: (id) => API.deleteSimulation(brainId!, id),
    onSuccess: (_resp, id) => {
      onSimulationRemoved(queryClient, brainId!, id);
      queryClient.invalidateQueries({ queryKey: [endpoints.schedules(brainId!)] });
      toast.success(t('simulations.deleted'));
    },
    meta: { errorToast: true },
  });

  const { mutateAsync: bulkDeleteSimulations } = useMutation<void, Error, string[]>({
    mutationFn: async (ids) => {
      const results = await runWithConcurrency(ids, BULK_DELETE_CONCURRENCY, (id) =>
        API.deleteSimulation(brainId!, id)
      );

      const failedCount = results.filter((r) => r.status === 'rejected').length;

      // Update cache for successfully deleted items
      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          onSimulationRemoved(queryClient, brainId!, ids[i]);
        }
      });

      // Schedules' simulation_ids may have lost entries server-side
      queryClient.invalidateQueries({ queryKey: [endpoints.schedules(brainId!)] });

      if (failedCount > 0) {
        // Refetch to reconcile any stale state
        queryClient.invalidateQueries({ queryKey: [endpoints.simulations(brainId!)] });
        throw new Error(t('simulations.bulk_delete_partial', { 0: failedCount }));
      }
    },
    onSuccess: (_resp, ids) => {
      toast.success(t('simulations.bulk_deleted', { 0: ids.length }));
    },
    meta: { errorToast: true },
  });

  const { mutate: triggerRun, status: triggerRunStatus } = useMutation<
    SimulationRun,
    Error,
    { simulation_ids?: string[]; filter_category?: string } | undefined
  >({
    mutationFn: (body) => API.triggerRun(brainId!, body ?? undefined),
    meta: {
      errorToast: true,
      segment: {
        success: EventName.SimulationRunStarted,
        action: 'simulation_run',
        successProps: (run: SimulationRun) => ({ run_id: run.run_id, brain_id: run.brain_id }),
      },
    },
    onSuccess: (run) => {
      queryClient.invalidateQueries({ queryKey: [endpoints.runs(brainId!)] });

      toast.info(t('simulations.run_started'), {
        description: t('simulations.run_started_description'),
        duration: 10_000,
        action: { label: t('simulations.view'), onClick: () => navigateToRun(run.run_id) },
      });

      waitForTerminal(queryClient, brainId!, run.run_id).then(() => {
        queryClient.invalidateQueries({ queryKey: [endpoints.runs(brainId!)] });
        toast.success(t('simulations.run_completed'), {
          description: t('simulations.run_completed_description'),
          action: { label: t('simulations.view'), onClick: () => navigateToRun(run.run_id) },
        });
      });
    },
  });

  const { mutate: cancelRun } = useMutation<void, Error, string>({
    mutationFn: (runId) => API.cancelRun(brainId!, runId),
    onSuccess: (_resp, runId) => {
      queryClient.invalidateQueries({ queryKey: [endpoints.run(brainId!, runId)] });
      queryClient.invalidateQueries({ queryKey: [endpoints.runs(brainId!)] });
      queryClient.invalidateQueries({ queryKey: [endpoints.evaluations(brainId!, runId)] });
      toast.success(t('simulations.run_canceled'));
    },
    meta: { errorToast: true },
  });

  const { mutate: deleteRun } = useMutation<void, Error, string>({
    mutationFn: (runId) => API.deleteRun(brainId!, runId),
    onSuccess: (_resp, runId) => {
      onRunRemoved(queryClient, brainId!, runId);
      toast.success(t('simulations.run_deleted'));
    },
    meta: { errorToast: true },
  });

  const simulations = simulationsData?.simulations;
  const categories = useMemo(() => listCategories(simulations ?? []), [simulations]);

  return {
    simulations,
    categories,
    listStatus,
    isSimulationsLoading,
    createStatus,
    updateStatus,
    deleteStatus,
    triggerRunStatus,
    createSimulation,
    updateSimulation,
    deleteSimulation,
    bulkDeleteSimulations,
    triggerRun,
    cancelRun,
    deleteRun,
  };
};

export const useSimulationRun = (brainId?: string, runId?: string) => {
  const {
    data: run,
    isLoading,
    error,
  } = useQuery<SimulationRun, ApiError>({
    queryKey: [endpoints.run(brainId!, runId!)],
    queryFn: () => API.getRun(brainId!, runId!),
    enabled: !!brainId && !!runId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status) {
        return false;
      }
      return isRunTerminal(status) ? false : FIVE_SECONDS;
    },
  });

  return { run, isLoading, error };
};

export const useEvaluations = (brainId?: string, runId?: string) => {
  const { data: evaluationsData, isLoading } = useQuery<EvaluationsListResponse>({
    queryKey: [endpoints.evaluations(brainId!, runId!)],
    queryFn: () => API.listEvaluations(brainId!, runId!),
    enabled: !!brainId && !!runId,
    refetchInterval: (query) => {
      const evals = query.state.data?.evaluations;
      const hasActive = evals?.some((e: EvaluationWithSimulation) =>
        isEvaluationInProgress(e.status)
      );
      return hasActive ? FIVE_SECONDS : false;
    },
  });

  return { evaluations: evaluationsData?.evaluations, isLoading };
};

export const useEvaluationInsights = (brainId: string, runId: string, evaluationId: string) => {
  const { brain } = useBrains(brainId);
  const brainVersion = brain?.version ?? null;

  const { cachedData, write } = useScopedCache(insightsCache, evaluationId, brainVersion);

  const mutation = useMutation<EvaluationInsightsResponse, Error>({
    mutationKey: [endpoints.evaluationInsights(brainId, runId, evaluationId)],
    mutationFn: () => API.runEvaluationInsights(brainId, runId, evaluationId),
    onSuccess: (data) => {
      if (brainVersion !== null) {
        write(data, brainVersion);
      }
    },
  });

  return { ...mutation, data: mutation.data ?? cachedData ?? undefined };
};

export default useSimulations;
